import { useState } from "react";
import { CLOUD_ENGINES } from "../lib/engines.generated";

// Wizard step 1, under the drop box: "Which app are your messages in?" — an
// accordion whose rows come from the engine-catalog manifest (CLOUD_ENGINES).
// Each row opens to that engine's export how-to. Adding an engine to
// engines.yaml (+ sync.py) adds a row here automatically — no edit to this file.
// The CSV fallback is a fixed final row: it's the catch-all, not an advertised
// engine, so it deliberately isn't in the manifest list.

type HelpRow = {
  id: string;
  label: string;
  tag: string;
  howTo: string;
  helpUrl?: string;
};

const CSV_FALLBACK: HelpRow = {
  id: "csv",
  label: "Something else — a spreadsheet",
  tag: "CSV",
  howTo:
    "Any messages you can get into a spreadsheet work too: one row per " +
    "message, with columns for the date, who sent it, and what it said. Save " +
    "it as a CSV and drop it above — Aurora shows what it read and lets you " +
    "fix the columns if anything looks off.",
};

export function FormatHelp() {
  const [openId, setOpenId] = useState<string | null>(null);

  const rows: HelpRow[] = [
    ...CLOUD_ENGINES.map((e) => ({
      id: e.id,
      label: e.display,
      tag: "PDF",
      howTo: e.howTo,
      helpUrl: e.helpUrl,
    })),
    CSV_FALLBACK,
  ];

  return (
    <section className="format-help" aria-label="Help getting your file">
      <h2 className="format-help-title">Need help getting your file?</h2>
      <p className="format-help-sub">
        Already have your export? Just drop it above. Otherwise, here&rsquo;s
        how to get it from each app.
      </p>
      <div className="format-help-list">
        {rows.map((r) => {
          const isOpen = openId === r.id;
          return (
            <div
              key={r.id}
              className={`format-help-row${isOpen ? " open" : ""}`}
            >
              <button
                type="button"
                className="format-help-q"
                aria-expanded={isOpen}
                onClick={() => setOpenId(isOpen ? null : r.id)}
              >
                <span className="format-help-app">
                  {r.label}
                  <span className="format-help-tag">{r.tag}</span>
                </span>
                <span className="format-help-chev" aria-hidden="true">
                  {isOpen ? "▾" : "▸"}
                </span>
              </button>
              {isOpen ? (
                <div className="format-help-a">
                  <p className="format-help-how">{r.howTo}</p>
                  {r.helpUrl ? (
                    <a
                      className="format-help-link"
                      href={r.helpUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {r.label}&rsquo;s export guide&nbsp;→
                    </a>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}
